import { router } from '@inertiajs/react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { MarketProjectOption } from '@/types';
import type { MarketStatus } from '@/types/market';

export function MarketLifecycle({
  eventSlug,
  status,
  winnerProjectId,
  projects,
  error,
}: {
  eventSlug: string;
  status: MarketStatus;
  winnerProjectId: string | null;
  projects: MarketProjectOption[];
  error?: string;
}) {
  const [pending, setPending] = useState(false);
  const [winnerDraft, setWinnerDraft] = useState<string>(winnerProjectId ?? '');

  const options = { preserveScroll: true, onFinish: () => setPending(false) };

  const transition = (to: MarketStatus) => {
    setPending(true);
    router.post(`/admin/${eventSlug}/market/transition`, { to }, options);
  };

  const setWinner = () => {
    setPending(true);
    router.post(
      `/admin/${eventSlug}/market/winner`,
      { winnerProjectId: winnerDraft },
      options,
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm">
        Current status: <span className="font-mono">{status}</span>
      </p>

      <div className="flex flex-wrap gap-2">
        {status === 'setup' && (
          <Button
            className="rounded-none"
            onClick={() => transition('open')}
            disabled={pending}
          >
            Open market
          </Button>
        )}
        {status === 'open' && (
          <Button
            className="rounded-none"
            variant="destructive"
            onClick={() => transition('closed')}
            disabled={pending}
          >
            Close market
          </Button>
        )}
        {status === 'closed' && (
          <Button
            className="rounded-none"
            variant="outline"
            onClick={() => transition('open')}
            disabled={pending}
          >
            Re-open
          </Button>
        )}
      </div>

      {(status === 'closed' || status === 'resolved') && (
        <div className="flex flex-col gap-2 border-t pt-4">
          <Label>Winning project</Label>
          <div className="flex gap-2">
            <Select value={winnerDraft} onValueChange={setWinnerDraft}>
              <SelectTrigger className="flex-1 rounded-none">
                <SelectValue placeholder="Select winner…" />
              </SelectTrigger>
              <SelectContent>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              className="rounded-none"
              onClick={setWinner}
              disabled={pending || !winnerDraft}
            >
              {status === 'resolved' ? 'Update winner' : 'Resolve'}
            </Button>
          </div>
          {status === 'resolved' && (
            <p className="text-muted-foreground text-xs">
              Already resolved. Setting a new winner overrides the result.
            </p>
          )}
        </div>
      )}

      {error && (
        <p className="bg-destructive/10 p-2 text-destructive text-sm">{error}</p>
      )}
    </div>
  );
}
